import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Smart Photo Selection
// ============================================================================

const CARD_KEYWORDS: Record<string, string[]> = {
  'virtual-tour': ['interior', 'apartment', 'unit', 'bedroom', 'living room', 'kitchen', 'bathroom', 'floor plan', 'inside', 'home', 'tour'],
  gallery: ['community', 'building', 'property', 'exterior', 'entrance', 'lobby', 'leasing', 'welcome', 'sign', 'front'],
  neighborhood: ['aerial', 'drone', 'skyline', 'neighborhood', 'exterior', 'bird', 'view', 'surrounding', 'location', 'city', 'street'],
  amenities: ['pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'recreation', 'courtyard', 'grill', 'dog park', 'playground', 'spa', 'theater', 'game'],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  cardKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = CARD_KEYWORDS[cardKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const keys = ['virtual-tour', 'gallery', 'neighborhood', 'amenities'] as const;
    const fallbacks = [1, 2, 4, 5];
    const indices: number[] = [];
    for (let i = 0; i < keys.length; i++) {
      const idx = findBestPhotoIndex(gallery, keys[i], used, fallbacks[i]);
      used.add(idx);
      indices.push(idx);
    }
    return indices as [number, number, number, number];
  }, [gallery]);
}

function makeSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const card1ImageDef: SlotGroupDefinition = {
  label: 'Virtual Tour Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'Pick a photo of an apartment interior — living area, bedroom, or kitchen — that helps a prospect picture living there.',
    context: 'Background image for the Virtual Tour card on the home page. One of four CTA cards; each must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Gallery Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'Pick a photo that shows the property exterior, entrance, or building — a strong first-impression shot of the community.',
    context: 'Background image for the Gallery card on the home page. One of four CTA cards; each must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Neighborhood Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'Pick a photo that captures the surrounding area — an aerial view, street scene, or neighborhood shot near the property.',
    context: 'Background image for the Neighborhood card on the home page. One of four CTA cards; each must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card4ImageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'Pick a photo of a standout amenity — pool, fitness center, clubhouse, or outdoor space.',
    context: 'Background image for the Amenities card on the home page. One of four CTA cards; each must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

// ============================================================================
// Component
// ============================================================================

export function CTASection() {
  const [idx1, idx2, idx3, idx4] = useSmartPhotoIndices();

  // Runtime slot defs with smart photo indices
  const def1 = useMemo(() => makeSlotDef('Virtual Tour Image', idx1), [idx1]);
  const def2 = useMemo(() => makeSlotDef('Gallery Image', idx2), [idx2]);
  const def3 = useMemo(() => makeSlotDef('Neighborhood Image', idx3), [idx3]);
  const def4 = useMemo(() => makeSlotDef('Amenities Image', idx4), [idx4]);

  const img1 = useSlotGroup('ctasection.card1_image', def1);
  const img2 = useSlotGroup('ctasection.card2_image', def2);
  const img3 = useSlotGroup('ctasection.card3_image', def3);
  const img4 = useSlotGroup('ctasection.card4_image', def4);

  const title1 = useContentToken('ctasection.card1_title');
  const title2 = useContentToken('ctasection.card2_title');
  const title3 = useContentToken('ctasection.card3_title');
  const title4 = useContentToken('ctasection.card4_title');

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 150 });

  return (
    <section className="w-full bg-[var(--vibe-color-background)]">
      <div ref={headingRef} className="reveal-fade-up text-center py-12 md:py-16 px-4">
        <RichTextContent
          path="ctasection.title"
          label="Section Title"
          defaultValue='<p>Explore What All <span data-type="field-token" data-field="property_name"></span> Has To Offer</p>'
          as="h2"
          className="font-heading text-2xl sm:text-3xl md:text-4xl font-normal text-[var(--vibe-color-text)] tracking-wider leading-snug"
        />
        <div className="w-20 h-1 mx-auto mt-5 rounded-full cta-accent-bar" />
      </div>

      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-[280px_280px] gap-[3px]">
        {/* Card 1 — Virtual Tour */}
        <a data-reveal href="/virtual-tour" className="reveal-fade-up relative block md:row-span-2 h-[300px] md:h-auto overflow-hidden group cursor-pointer">
          <EditableSlotGroup groupPath="ctasection.card1_image" definition={def1}>
            {img1.image ? (
              <VibesitesImage url={img1.image} alt={img1.alt || ''} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110" />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
          <div className="absolute inset-0 cta-card-overlay pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-10">
            <RichTextContent path="ctasection.card1_subtitle" defaultValue='<p>Experience <span data-type="field-token" data-field="property_name"></span></p>' as="p" className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80 mb-1 drop-shadow-md" />
            <EditableToken path="ctasection.card1_title" defaultValue="Take A Virtual Tour" as="p" className="font-heading text-lg sm:text-xl md:text-2xl font-semibold text-white drop-shadow-lg leading-tight">{title1}</EditableToken>
          </div>
        </a>

        {/* Card 2 — Gallery */}
        <a data-reveal href="/gallery" className="reveal-fade-up relative block h-[250px] md:h-auto overflow-hidden group cursor-pointer">
          <EditableSlotGroup groupPath="ctasection.card2_image" definition={def2}>
            {img2.image ? (
              <VibesitesImage url={img2.image} alt={img2.alt || ''} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110" />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
          <div className="absolute inset-0 cta-card-overlay pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-10">
            <RichTextContent path="ctasection.card2_subtitle" defaultValue="<p>Picture Yourself Here</p>" as="p" className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80 mb-1 drop-shadow-md" />
            <EditableToken path="ctasection.card2_title" defaultValue="View The Gallery" as="p" className="font-heading text-lg sm:text-xl md:text-2xl font-semibold text-white drop-shadow-lg leading-tight">{title2}</EditableToken>
          </div>
        </a>

        {/* Card 3 — Neighborhood */}
        <a data-reveal href="/neighborhood" className="reveal-fade-up relative block h-[250px] md:h-auto overflow-hidden group cursor-pointer">
          <EditableSlotGroup groupPath="ctasection.card3_image" definition={def3}>
            {img3.image ? (
              <VibesitesImage url={img3.image} alt={img3.alt || ''} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110" />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
          <div className="absolute inset-0 cta-card-overlay pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-10">
            <RichTextContent path="ctasection.card3_subtitle" defaultValue='<p>Experience <span data-type="field-token" data-field="property_address_city"></span> Living</p>' as="p" className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80 mb-1 drop-shadow-md" />
            <EditableToken path="ctasection.card3_title" defaultValue="Explore The Neighborhood" as="p" className="font-heading text-lg sm:text-xl md:text-2xl font-semibold text-white drop-shadow-lg leading-tight">{title3}</EditableToken>
          </div>
        </a>

        {/* Card 4 — Amenities */}
        <a data-reveal href="/amenities" className="reveal-fade-up relative block md:col-span-2 h-[250px] md:h-auto overflow-hidden group cursor-pointer">
          <EditableSlotGroup groupPath="ctasection.card4_image" definition={def4}>
            {img4.image ? (
              <VibesitesImage url={img4.image} alt={img4.alt || ''} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110" />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
          <div className="absolute inset-0 cta-card-overlay pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-10">
            <RichTextContent path="ctasection.card4_subtitle" defaultValue="<p>Convenience All Around</p>" as="p" className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80 mb-1 drop-shadow-md" />
            <EditableToken path="ctasection.card4_title" defaultValue="Browse Our Amenities" as="p" className="font-heading text-lg sm:text-xl md:text-2xl font-semibold text-white drop-shadow-lg leading-tight">{title4}</EditableToken>
          </div>
        </a>
      </div>

      <style>{`
        .cta-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
        .cta-card-overlay {
          background: linear-gradient(
            to top,
            color-mix(in srgb, var(--vibe-color-primary) 50%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 15%, transparent) 40%,
            transparent 100%
          );
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'CTA Section',
  tokens: {
    'ctasection.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: '<p>Explore What All <span data-type="field-token" data-field="property_name"></span> Has To Offer</p>',
      generator: {
        prompt: 'An exploratory section title inviting visitors to discover what this community has to offer.',
        context: 'Heading above the four CTA cards on the home page.',
        length: '1 short phrase',
      },
    },
    'ctasection.card1_subtitle': {
      label: 'Virtual Tour Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experience <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'ctasection.card1_title': {
      label: 'Virtual Tour Title',
      type: 'text',
      defaultValue: 'Take A Virtual Tour',
    },
    'ctasection.card2_subtitle': {
      label: 'Gallery Subtitle',
      type: 'richtext',
      defaultValue: '<p>Picture Yourself Here</p>',
    },
    'ctasection.card2_title': {
      label: 'Gallery Title',
      type: 'text',
      defaultValue: 'View The Gallery',
    },
    'ctasection.card3_subtitle': {
      label: 'Neighborhood Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experience <span data-type="field-token" data-field="property_address_city"></span> Living</p>',
    },
    'ctasection.card3_title': {
      label: 'Neighborhood Title',
      type: 'text',
      defaultValue: 'Explore The Neighborhood',
    },
    'ctasection.card4_subtitle': {
      label: 'Amenities Subtitle',
      type: 'richtext',
      defaultValue: '<p>Convenience All Around</p>',
    },
    'ctasection.card4_title': {
      label: 'Amenities Title',
      type: 'text',
      defaultValue: 'Browse Our Amenities',
    },
  },
  slotGroups: {
    'ctasection.card1_image': card1ImageDef,
    'ctasection.card2_image': card2ImageDef,
    'ctasection.card3_image': card3ImageDef,
    'ctasection.card4_image': card4ImageDef,
  },
};

(CTASection as any).contentSchema = contentSchema;

export default CTASection;
